import React from 'react'
import { useIsMobile } from 'spa/member/hooks/v2/useIsMobile'
import number01 from 'images/spa/top/service_features/number_01.svg'
import number02 from 'images/spa/top/service_features/number_02.svg'
import number03 from 'images/spa/top/service_features/number_03.svg'
import number04 from 'images/spa/top/service_features/number_04.svg'
import supportPerson from 'images/spa/top/service_features/support_person.svg'
import Project from 'images/spa/top/service_features/project.svg'
import VariousProjects from 'images/spa/top/service_features/various_projects.svg'
import IncreasedReward from 'images/spa/top/service_features/increased_reward.svg'
import styles from './ServiceFeatureDetails.module.scss'
import { StyledServiceFeatureDetailsProps } from './types'
import { useFormatNumber } from 'spa/hooks/useFormatNumber'
import ScrollContainer from 'react-indiana-drag-scroll'

const featureImageHeader = (image: string, alt: string, text: string, className = '') => {
  return (
    <div className={`flex items-center gap-S pb-S ${className}`}>
      <div className="flex justify-center items-center rounded-full w-12 h-12 border-1 border-dashed border-member-green-100 mobile:w-10 mobile:h-10">
        <img src={image} width="22" height="16" alt={alt} className={styles.numberImage} />
      </div>
      <div className="text-M font-bold tablet:text-XL">{text}</div>
    </div>
  )
}

const imageAreaClass = 'w-full flex justify-center'
const serviceFeatureClass = 'px-M py-L tablet:px-2XL tablet:py-XL'
const featureImageSectionTopClass = 'grid grid-flow-col grid-cols-1 tablet:grid-cols-2'

const isFirstStep = (index: number) => index === 0

const isLastStep = (index: number, stepLength: number) => {
  return index === stepLength - 1
}

const getBadgeColorClass = (index: number, stepLength: number) => {
  if (isLastStep(index, stepLength)) {
    return 'border-member-gray-0 text-member-gray-0'
  }

  return 'border-member-gray-50 text-member-gray-50'
}

const getPriceAreaClass = (index: number, stepLength: number) => {
  if (isLastStep(index, stepLength)) {
    return `text-member-gray-0 bg-member-green-100 h-20 px-2XL rounded-M relative ${styles.lastStepPriceArea}`
  }

  return `desktop:py-XS ${styles.normalPriceArea}`
}

/* TODO: MaterialSymbolコンポーネントがマージされたら入れ替える */
const StepArrow = () => <span className={`material-symbols-outlined text-XS transform rotate-90 desktop:rotate-0 ${styles.stepArrow}`}>play_arrow</span>

export const StyledServiceFeatureDetails: React.FC<StyledServiceFeatureDetailsProps> = ({ workers }) => {
  const isMobile = useIsMobile()
  const { formatNumber } = useFormatNumber()

  return (
    <section className={`flex flex-col items-center pt-2XL w-full relative tablet:pt-4XL ${styles.featureDetailsContainer}`}>
      <h2 className="text-L font-bold pb-2XL text-center tablet:text-2XL tablet:pb-4XL">
        案件継続率90%以上！{isMobile && <br />}安定した稼働が可能です
      </h2>
      <section className={`border-t-1 border-member-gray-20 grid w-full ${styles.featureImages}`}>
        <div className={`tablet:border-r-1 ${serviceFeatureClass}`}>
          {featureImageHeader(number01, '01', '手厚いサポート体制')}
          <div className={imageAreaClass}>
            <img src={supportPerson} alt="サポート体制" loading="lazy" width="243" />
          </div>
          <ul className={`pt-S ${styles.featureDetailList}`}>
            <li>
              <span>初めて稼働の人は3か月間フォロー</span>
            </li>
            <li>
              <span>月一状況ヒアリング</span>
            </li>
            <li>
              <span>稼働初日には3者で条件や担当を確認</span>
            </li>
          </ul>
        </div>
        <div className={serviceFeatureClass}>
          {featureImageHeader(number02, '02', 'エンド案件がメイン')}
          <div className={imageAreaClass}>
            <img src={Project} alt="エンド案件" loading="lazy" width="296" />
          </div>
          <ul className={`pt-S ${styles.featureDetailList}`}>
            <li>
              <span>参画企業とコミュニケーションがとりやすい</span>
            </li>
            <li>
              <span>柔軟な働き方が可能</span>
            </li>
            <li>
              <span>面談1回の企業が多く成約スピードが速い</span>
            </li>
          </ul>
        </div>
        <div className={`${serviceFeatureClass} ${styles.featureImage03} border-t-1 border-member-gray-20`}>
          <div className={`${featureImageSectionTopClass} ${styles.featureImageSectionTop}`}>
            {featureImageHeader(number03, '03', '案件の業界の幅が広い', 'tablet:order-1')}
            <div className={[imageAreaClass, styles.featureImage03Area, 'tablet:order-3'].join(' ')}>
              <img src={VariousProjects} alt="多種多様な案件" loading="lazy" width="330" />
            </div>
            <div className="tablet:order-2">
              <p className={`mt-L text-S tablet:mt-0 tablet:text-M tablet:order-2 ${styles.featureImageParagraph}`}>
                IT・ベンチャーから自治体・大手まで多種多様な案件があるから、成長できる。
              </p>
            </div>
          </div>
        </div>
        <div className={`${serviceFeatureClass} ${styles.featureImage04} flex flex-col gap-XL border-t-1 border-member-gray-20`}>
          <div className={`${featureImageSectionTopClass} ${styles.featureImageSectionTop}`}>
            {featureImageHeader(number04, '04', '報酬単価UPの実績多数', 'tablet:order-1')}
            <div className={[imageAreaClass, styles.featureImage04Area, 'tablet:order-3'].join(' ')}>
              <img src={IncreasedReward} alt="報酬単価UP" loading="lazy" width="330" />
            </div>
            <div className="tablet:order-2">
              <p className={`mt-L text-S tablet:mt-0 tablet:text-M tablet:order-2 ${styles.featureImageParagraph}`}>
                全体では約10%のUP率。20～30%UPの人も。<br />エンド案件が多いことと、両者と適度な接触機会を設けているから成しえる数値。<br />適切な報酬をお届けします。
              </p>
              <div className="text-2XS text-member-gray-60 pt-S tablet:text-XS">※2024年7月1日時点。過去2年稼働実績ある方が対象。</div>
            </div>
          </div>
          <ScrollContainer className={`absolute flex gap-M tablet:static tablet:grid tablet:grid-cols-2 tablet:justify-center ${styles.workersScrollContainer}`}>
            {workers.map(worker => (
              <div className={`grid justify-center border-1 border-member-gray-20 rounded-M ${styles.workerContainer}`} key={worker.name}>
                <div className="flex flex-col items-center p-M tablet:justify-center tablet:px-2XL" style={{ width: isMobile ? '69.33vw' : 'auto' }}>
                  <img src={worker.image} alt={`${worker.name}さん`} width="64" height="64" loading="lazy" className="pb-XS" />
                  <div className={`text-S ${styles.workerName}`}>{worker.name}さん（{worker.occupation}）</div>
                  <div className="flex items-center w-full pt-M pb-S">
                    <hr className="border-t-member-gray-20 w-1/2 m-0" />
                    <div className="w-2 h-2 rounded-full bg-member-gray-20 mx-XS" />
                    <hr className="border-t-member-gray-20 w-1/2 m-0" />
                  </div>
                  <div className="flex flex-col justify-center gap-2XS h-100 desktop:flex-row">
                    {worker.steps.map((step, index) => (
                      <React.Fragment key={step.label}>
                        {isFirstStep(index) ? null : (
                          <div className={`flex flex-col items-center desktop:flex-row ${styles.stepArrows}`}>
                            {step.jump ? <><StepArrow /><StepArrow /></> : <StepArrow />}
                          </div>
                        )}
                        <div className={`flex justify-center items-center gap-2XS ${getPriceAreaClass(index, worker.steps.length)}`}
                          data-first-step={isFirstStep(index) ? 'true' : undefined}>
                          <div className="flex flex-col items-center">
                            {/* TODO: Badgeコンポーネントができたら入れ替える */}
                            <div
                              className={`inline-flex justify-center items-center border-1 text-2XS font-bold rounded-full px-XS ${getBadgeColorClass(index, worker.steps.length)}`}
                              style={{ height: '18px' }}>
                              {step.label}
                            </div>
                            <div className={`${isLastStep(index, worker.steps.length) ? 'text-L font-bold' : 'text-XS'} ${styles.price}`}>
                              {formatNumber(step.price)}円
                            </div>
                          </div>
                          {isLastStep(index, worker.steps.length) && (
                            <div className={`absolute flex flex-col justify-center items-center font-bold z-0 ${styles.lastStepBubble}`}>
                              <div style={{ fontSize: '11px' }}>開始から</div>
                              <div>
                                <span className="text-L">{worker.percent}</span>
                                <span className="text-XS">%</span>
                                <span style={{ fontSize: '11px' }}>UP</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </ScrollContainer>
        </div>
      </section>
    </section>
  )
}
